import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function DepositReview({ target, onClose, onApproved }) {
  const [network, setNetwork] = useState("TRX");
  const [hash, setHash] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const networks = { TRX: "TRON (TRC20)", ETH: "Ethereum (ERC20)", BSC: "BNB Smart Chain (BEP20)" };
  const explorers = { TRX: "https://tronscan.org/#/transaction/", ETH: "https://etherscan.io/tx/", BSC: "https://bscscan.com/tx/" };
  const contracts = { TRX: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t", ETH: "0xdac17f958d2ee523a2206206994597c13d831ec7", BSC: "0x55d398326f99059ff775485246999027b3197955" };
  const validHash = /^(?:0x)?[0-9a-f]{64}$/i.test(hash.trim());
  const address = target.deposit_addresses?.[network];
  const approve = async (event) => {
    event.preventDefault();
    if (saving) return;
    setError("");
    const received = amount.trim();
    const decimals = network === "BSC" ? 18 : 6;
    if (!address) { setError("This user has no assigned receiving address on the selected network."); return; }
    if (!validHash) { setError("Enter the complete 64-character transaction hash, with an optional 0x prefix."); return; }
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(received) || !/[1-9]/.test(received)) {
      setError("Enter the positive USDT amount actually received, using digits and a decimal point."); return;
    }
    if ((received.split(".")[1] || "").replace(/0+$/, "").length > decimals) {
      setError(`This network supports up to ${decimals} decimal places for USDT.`); return;
    }
    if (!confirmed) { setError("Check the explorer verification box before approving this deposit."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/deposits/approve`, {
        network, tx_hash: hash.trim(), amount: received, note: note.trim(), confirmed_in_explorer: confirmed,
      });
      toast.success(data.message);
      onApproved();
      onClose();
    } catch (err) {
      const message = err.response
        ? formatApiError(err.response.data?.detail)
        : err.code === "ECONNABORTED"
          ? "The approval request timed out. Refresh the balance, then retry the same transaction if needed."
          : "Could not reach the backend. Check your connection and try the same transaction again.";
      setError(message);
      toast.error(message);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="bg-[#121721] border-[#1E293B] text-white max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Review USDT deposit</DialogTitle></DialogHeader>
        <form onSubmit={approve} noValidate className="space-y-3 text-xs">
          <p>{target.email}</p>
          <label className="block">Network
            <select value={network} onChange={(e) => { setNetwork(e.target.value); setConfirmed(false); }} className="mt-1 w-full bg-[#0B0E14] border border-[#1E293B] p-2 rounded">
              {Object.entries(networks).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          <p className="break-all text-slate-400">Receiving address: {address || "No assigned address"}</p>
          <p className="break-all text-slate-400">Accepted USDT contract: {contracts[network]}</p>
          <label className="block">Transaction hash
            <Input required value={hash} onChange={(e) => { setHash(e.target.value); setConfirmed(false); }} className="mt-1 bg-[#0B0E14]" />
          </label>
          {validHash && <a href={explorers[network] + (network === "TRX" ? hash.trim().replace(/^0x/i, "") : "0x" + hash.trim().replace(/^0x/i, ""))} target="_blank" rel="noreferrer" className="block text-[#00E676] underline">Open transaction in explorer</a>}
          <label className="block">Total USDT received at this address in this transaction
            <Input required inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setConfirmed(false); }} placeholder="Exact received amount, e.g. 3.5" className="mt-1 bg-[#0B0E14]" />
          </label>
          <label className="block">Review note (optional)
            <Textarea maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any additional details about your review" className="mt-1 bg-[#0B0E14]" />
          </label>
          <label className="flex items-start gap-2 text-slate-300">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
            I verified successful execution, final confirmation, the token contract, this receiving address, and the full received amount. This approval credits the account without an automatic chain check.
          </label>
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <Button type="submit" disabled={saving} className="w-full bg-[#00E676] text-black">
            {saving ? "Saving approval…" : `Approve ${amount || "0"} USDT`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
